import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import {
  Target, ArrowRight, FileSearch, FileCheck2, PenLine, Mic2, DollarSign,
  UsersRound, UploadCloud, Radar, Send, ShieldCheck, Globe2, Briefcase,
} from "lucide-react";
import { FaLinkedin } from "react-icons/fa";
import { SiIndeed, SiGlassdoor } from "react-icons/si";
import InstallPrompt from "@/components/InstallPrompt";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const AGENTS = [
  { icon: FileCheck2, title: "ATS Optimizer", desc: "Beats the resume-screening bots. Scores your resume against any job description and rewrites weak sections keyword-by-keyword.", span: "md:col-span-8", featured: true },
  { icon: FileSearch, title: "Resume Parser", desc: "Upload once — Claude extracts your skills, experience and strengths into a living profile.", span: "md:col-span-4" },
  { icon: Mic2, title: "Interview Coach", desc: "Realistic mock interviews for your exact target role, with instant feedback on every answer.", span: "md:col-span-4" },
  { icon: PenLine, title: "Cover Letter Writer", desc: "Role-specific letters in your voice — never generic, never copy-paste.", span: "md:col-span-4" },
  { icon: DollarSign, title: "Salary Insights", desc: "Know your market value in any city before you negotiate.", span: "md:col-span-4" },
  { icon: UsersRound, title: "Warm-Intro Referrals", desc: "Finds who you know at target companies and drafts the intro message.", span: "md:col-span-4" },
];

const STEPS = [
  { icon: UploadCloud, title: "Upload your resume", desc: "Our CV agent parses it into a rich career profile in seconds." },
  { icon: Radar, title: "AI scores every match", desc: "Live jobs from LinkedIn, Indeed, Glassdoor & Naukri, ranked against your real skills." },
  { icon: Send, title: "Tailor & apply — with approval", desc: "One click tailors your resume and cover letter. You approve, the agent applies." },
];

function NaukriMark() {
  return <span className="font-bold tracking-tight text-lg">naukri<span className="text-cyan-300">.com</span></span>;
}

export default function LandingPage() {
  return (
    <div className="min-h-screen text-zinc-200" style={{ background: "#0a0a1a", fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="landing-page">
      <InstallPrompt />

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0a0a1a]/70 border-b border-violet-500/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="landing-logo">
            <img src="/logo.png" alt="Curcompass logo" className="w-9 h-9 rounded-xl object-cover" />
            <div>
              <div className="font-bold text-white leading-none">Curcompass</div>
              <div className="text-[9px] tracking-[0.25em] text-violet-300">AI CAREER COPILOT</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#agents" className="hover:text-white transition-colors" data-testid="nav-agents">Agents</a>
            <a href="#how" className="hover:text-white transition-colors" data-testid="nav-how">How it works</a>
            <a href="#pricing" className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="jr-btn-ghost text-sm py-2 px-4 hidden sm:block" data-testid="header-signin-btn">Sign In</Link>
            <Link to="/login" className="jr-btn-primary text-sm py-2 px-4" data-testid="header-cta-btn">Get Started Free</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(ellipse 60% 50% at 70% 20%, rgba(139,92,246,0.5), transparent), radial-gradient(ellipse 50% 40% at 20% 80%, rgba(34,211,238,0.25), transparent)" }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <motion.div {...fadeUp} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-violet-300 bg-violet-500/10 border border-violet-500/30 rounded-full px-4 py-1.5 mb-8" data-testid="hero-badge">
              <Globe2 className="w-3.5 h-3.5" /> 100% free during global launch
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Your unfair advantage<br />
              <span className="jr-gradient-text">in the job market.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-xl leading-relaxed">
              Six AI agents hunt jobs for you 24/7 — parsing your resume, scoring live openings from four job boards,
              tailoring every application, and prepping you for the interview.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link to="/login" className="jr-btn-primary text-base py-3.5 px-7 inline-flex items-center gap-2 group" data-testid="hero-cta-button">
                Start free — no card needed
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                Google or email sign-in · delete your data anytime
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Job sources marquee */}
      <section className="border-y border-white/5 bg-[#0c0c22] py-5" data-testid="sources-marquee">
        <Marquee speed={28} gradient={false} pauseOnHover>
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-14 pr-14 text-zinc-500">
              <span className="text-xs uppercase tracking-[0.25em]">Live scraping from</span>
              <span className="flex items-center gap-2 text-lg"><FaLinkedin className="text-2xl" /> LinkedIn</span>
              <span className="flex items-center gap-2 text-lg"><SiIndeed className="text-2xl" /> Indeed</span>
              <span className="flex items-center gap-2 text-lg"><SiGlassdoor className="text-2xl" /> Glassdoor</span>
              <NaukriMark />
              <span className="flex items-center gap-2 text-lg"><Briefcase className="w-5 h-5" /> 40+ countries</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* Agents bento */}
      <section id="agents" className="max-w-7xl mx-auto px-6 md:px-12 py-24 lg:py-32">
        <motion.div {...fadeUp}>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-400 mb-3">The agent team</div>
          <h2 className="text-lg md:text-lg font-bold text-white max-w-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Six specialists working your job hunt — powered by Claude.
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-12">
          {AGENTS.map((a, i) => (
            <motion.div
              key={a.title}
              {...fadeUp}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className={`jr-card relative overflow-hidden group hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(139,92,246,0.15)] transition-transform duration-300 ${a.span} ${a.featured ? "md:p-10" : ""}`}
              data-testid={`agent-card-${i}`}
            >
              {a.featured && (
                <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-violet-500/15 blur-3xl" />
              )}
              <div className={`rounded-xl grid place-items-center bg-violet-500/15 text-violet-300 ${a.featured ? "w-14 h-14" : "w-11 h-11"}`}>
                <a.icon className={a.featured ? "w-7 h-7" : "w-5 h-5"} />
              </div>
              <h3 className={`text-white font-semibold mt-4 ${a.featured ? "text-2xl" : "text-base"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{a.title}</h3>
              <p className={`text-zinc-400 mt-2 leading-relaxed ${a.featured ? "text-base max-w-xl" : "text-sm"}`}>{a.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-[#0c0c22] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 lg:py-32 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeUp}>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-400 mb-3">How it works</div>
            <h2 className="text-lg font-bold text-white mb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>
              From resume to interview in three moves.
            </h2>
            <div className="space-y-8">
              {STEPS.map((s, i) => (
                <div key={s.title} className="flex gap-5" data-testid={`step-${i + 1}`}>
                  <div className="flex flex-col items-center">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-400 grid place-items-center shrink-0 shadow-lg shadow-violet-500/25">
                      <s.icon className="w-5 h-5 text-white" />
                    </div>
                    {i < STEPS.length - 1 && <div className="w-px flex-1 bg-gradient-to-b from-violet-500/40 to-transparent mt-2" />}
                  </div>
                  <div className="pb-2">
                    <div className="text-white font-semibold">{i + 1}. {s.title}</div>
                    <p className="text-sm text-zinc-400 mt-1 leading-relaxed max-w-md">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.15 }} className="relative hidden lg:block">
            <img
              src="https://images.unsplash.com/photo-1622151834677-70f982c9adef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Professional using Curcompass on a laptop"
              className="rounded-3xl border border-violet-500/20 shadow-2xl shadow-violet-500/10 object-cover w-full h-[460px]"
              loading="lazy"
            />
            <div className="absolute -bottom-5 -left-5 jr-card border border-violet-500/30 flex items-center gap-3 py-3 px-4">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-sm text-zinc-300">Agent applied to 3 jobs while you slept</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing / free launch */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 md:px-12 py-24 lg:py-32">
        <motion.div
          {...fadeUp}
          className="relative overflow-hidden rounded-3xl border border-violet-500/30 p-10 lg:p-16 text-center"
          style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(34,211,238,0.08)), #11112a" }}
          data-testid="pricing-block"
        >
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[480px] h-[280px] rounded-full bg-violet-500/20 blur-3xl" />
          <div className="relative">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300 mb-4">Global launch offer</div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              100% free.<br className="sm:hidden" /> <span className="jr-gradient-text">No limits. No card.</span>
            </h2>
            <p className="text-zinc-400 mt-5 max-w-lg mx-auto">
              Every agent, every job board, unlimited searches — free while we launch worldwide.
              Premium plans (₹399/mo) arrive later; early users keep priority perks.
            </p>
            <Link to="/login" className="jr-btn-primary text-base py-3.5 px-8 inline-flex items-center gap-2 mt-9 group" data-testid="pricing-cta-btn">
              Claim your free account <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/5 overflow-hidden" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-10">
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                <img src="/logo.png" alt="Curcompass logo" className="w-8 h-8 rounded-lg object-cover" />
                <span className="font-bold text-white">Curcompass</span>
              </div>
              <p className="text-sm text-zinc-500 max-w-xs">Your AI agent team, hunting jobs for you 24/7.</p>
            </div>
            <div className="grid grid-cols-2 gap-x-16 gap-y-2 text-sm">
              <div className="space-y-2 flex flex-col">
                <span className="text-zinc-500 uppercase text-xs tracking-widest mb-1">Legal</span>
                <Link to="/terms" className="text-zinc-400 hover:text-white" data-testid="footer-terms-link">Terms & Conditions</Link>
                <Link to="/privacy" className="text-zinc-400 hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link>
                <Link to="/refund-policy" className="text-zinc-400 hover:text-white" data-testid="footer-refund-link">Refund & Pricing Policy</Link>
              </div>
              <div className="space-y-2 flex flex-col">
                <span className="text-zinc-500 uppercase text-xs tracking-widest mb-1">Support</span>
                <Link to="/contact" className="text-zinc-400 hover:text-white" data-testid="footer-contact-link">Contact Us</Link>
                <a href="mailto:customersupport@curcompass.com" className="text-zinc-400 hover:text-white" data-testid="footer-email-link">customersupport@curcompass.com</a>
                <Link to="/login" className="text-zinc-400 hover:text-white" data-testid="footer-signin-link">Sign In</Link>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/5 text-xs text-zinc-600">
            © 2026 Curcompass · Built with Claude Sonnet 4.5, Apify & MongoDB
          </div>
        </div>
        <div className="pointer-events-none select-none absolute -bottom-8 left-0 right-0 text-center font-black tracking-tighter text-white opacity-[0.04] text-[18vw] leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>
          CURCOMPASS
        </div>
      </footer>
    </div>
  );
}
